import { useMemo } from 'react'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { Tooltip } from '@mui/material'
import { GridActionsCellItem, GridRowId } from '@mui/x-data-grid'

import { IGridColumn } from 'components/common'
import { BookingDriver, BookingStatus, DriverAllocationStatus, DriverType } from 'generated/graphql'
import { isSupplierAllowedToDeleteDriver, isSupplierAllowedToReplaceDriver } from 'helpers'

const useGetAllocatedDriversColumns = (
  isAllowedToViewPrimaryDriversList: boolean,
  isAllowedToViewCurrentSupplierSubDriversList: boolean,
  handleDriverNotes: (note: string) => () => void,
  deleteBookingDriverProcessing: boolean,
  handleEditAllocatedDriver: (row: BookingDriver) => () => void,
  handleAddRating: (id: GridRowId) => () => void,
  handleDeleteDriver: (id: GridRowId) => () => void,
  bookingStatus: BookingStatus | undefined,
  isCxManualAcceptEnabled: boolean,
  handleCxManualAcceptOpenDialog: () => void,
  courierExchangeLoadId?: string,
) => {
  const isAllowedToRate =
    bookingStatus &&
    [BookingStatus.Invoice, BookingStatus.Complete, BookingStatus.ProofOfDelivery].includes(
      bookingStatus,
    )

  const EDIT_DISABLED_STATUSES_ADMIN = [BookingStatus.Invoice, BookingStatus.Complete]

  const isAllowedToEditDriver = (() => {
    if (!bookingStatus) return false

    if (isAllowedToViewPrimaryDriversList) {
      return !EDIT_DISABLED_STATUSES_ADMIN.includes(bookingStatus)
    }

    if (isAllowedToViewCurrentSupplierSubDriversList) {
      return isSupplierAllowedToReplaceDriver(bookingStatus)
    }

    return false
  })()

  const isAllowedToDeleteDriver = (() => {
    if (!bookingStatus) return false

    if (isAllowedToViewPrimaryDriversList) {
      return !EDIT_DISABLED_STATUSES_ADMIN.includes(bookingStatus)
    }

    if (isAllowedToViewCurrentSupplierSubDriversList) {
      return isSupplierAllowedToDeleteDriver(bookingStatus)
    }

    return false
  })()

  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'primaryDriverName',
        headerName: 'Company',
        minWidth: 150,
        flex: 1,
        sortable: false,
        valueGetter: ({ row }) => row?.driver.primaryDriverName,
      },
      {
        field: 'driver',
        headerName: 'Driver',
        minWidth: 150,
        flex: 1,
        sortPath: ['driver', 'name'],
        valueGetter: ({ value }) => value?.name,
      },
      { field: 'allocationStatus', headerName: 'Alloc. Status', width: 100 },
      {
        field: 'isUsingInternalApp',
        headerName: 'Using App?',
        width: 100,
        type: 'boolean',
        sortable: false,
        valueGetter: ({ row }) => row.driver?.isUsingInternalApp,
      },
      {
        field: 'reference',
        headerName: 'Reference',
        width: 100,
        type: 'string',
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        width: 120,
        sortPath: ['vehicle', 'vehicleType', 'name'],
        valueGetter: ({ value }) => value?.vehicleType?.name,
      },
      { field: 'charge', headerName: 'Charge', width: 80, type: 'number' },
      { field: 'extraPayment', headerName: 'Extra Pay', width: 80, type: 'number' },
      {
        field: 'currency',
        headerName: 'Ccy',
        width: 50,
        sortPath: ['currency', 'code'],
        valueGetter: ({ value }) => value?.code,
      },
      ...(isAllowedToViewPrimaryDriversList
        ? [
            {
              field: 'xeroPurchaseCode',
              headerName: 'Purchase Code',
              width: 120,
              type: 'number',
            },
          ]
        : []),
      { field: 'createdAt', headerName: 'Created At', hide: true },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        type: 'actions',
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={`note-${id}`}
            label='Driver note'
            size='medium'
            disabled={!row.driverNotes}
            icon={
              <Tooltip title={'Driver note'} arrow>
                <StickyNote2OutlinedIcon />
              </Tooltip>
            }
            onClick={handleDriverNotes(row.driverNotes)}
          />,
          <GridActionsCellItem
            key={`edit-${id}`}
            label='Edit'
            size='medium'
            disabled={deleteBookingDriverProcessing || !isAllowedToEditDriver}
            icon={
              <Tooltip title={'Edit'} arrow>
                <EditIcon />
              </Tooltip>
            }
            onClick={handleEditAllocatedDriver(row)}
          />,
          <GridActionsCellItem
            sx={{ display: isAllowedToRate ? 'block' : 'none' }}
            key={`rating-${id}`}
            label='Rating'
            size='medium'
            icon={
              <Tooltip title={'Rating'} arrow>
                {row.rating ? <StarIcon sx={{ color: '#faaf00' }} /> : <StarBorderIcon />}
              </Tooltip>
            }
            onClick={handleAddRating(row.driver.id)}
          />,
          <GridActionsCellItem
            sx={{
              display: isCxManualAcceptEnabled ? 'flex' : 'none',
            }}
            key={`cx-manual-accept-${id}`}
            label='CX Manual Accept'
            size='medium'
            icon={
              <Tooltip title={'CX Manual Accept Booking'} arrow>
                <CheckBoxIcon />
              </Tooltip>
            }
            disabled={
              row.allocationStatus === DriverAllocationStatus.Accepted ||
              row.allocationStatus === DriverAllocationStatus.Declined ||
              row.driver?.driverType !== DriverType.Cx ||
              !courierExchangeLoadId
            }
            onClick={handleCxManualAcceptOpenDialog}
          />,
          <GridActionsCellItem
            key={`delete-${id}`}
            label='Delete'
            size='medium'
            disabled={deleteBookingDriverProcessing || !isAllowedToDeleteDriver}
            icon={
              <Tooltip title={'Delete'} arrow>
                <DeleteIcon />
              </Tooltip>
            }
            onClick={handleDeleteDriver(id)}
          />,
        ],
      },
    ],
    [
      courierExchangeLoadId,
      deleteBookingDriverProcessing,
      handleAddRating,
      handleCxManualAcceptOpenDialog,
      handleDeleteDriver,
      handleDriverNotes,
      handleEditAllocatedDriver,
      isAllowedToDeleteDriver,
      isAllowedToEditDriver,
      isAllowedToRate,
      isAllowedToViewPrimaryDriversList,
      isCxManualAcceptEnabled,
    ],
  )

  return columns
}
export default useGetAllocatedDriversColumns
